import { Button, Tooltip } from "antd";
import { ChevronLeft, ChevronRight, Flame, LogOut } from "lucide-react";
import { Link } from "react-router-dom";

import { IconComponent } from "@/components/IconComponent";
import { cn } from "@/lib/cn";
import type { NavItem } from "@/routes/menuConfig";

interface SidebarLogoProps {
  /** 侧栏是否折叠 */
  collapsed: boolean;
  /** 切换折叠 */
  onToggleCollapse: () => void;
}

/** 侧栏 Logo 与折叠按钮 */
export const SidebarLogo = ({ collapsed, onToggleCollapse }: SidebarLogoProps) => {
  return (
    <div
      className={cn(
        "flex items-center shrink-0 relative overflow-hidden",
        collapsed ? "h-18 justify-center" : "h-18 px-6 justify-between",
      )}
    >
      <div className="absolute bottom-0 inset-x-0 h-0.5 bg-linear-to-r from-orange-400 via-amber-400 to-orange-300/50" />
      {!collapsed && (
        <div className="flex items-center gap-3.5">
          <div className="relative">
            <div className="w-10 h-10 rounded-xl bg-linear-to-br from-orange-500 via-amber-500 to-yellow-400 flex items-center justify-center shadow-lg shadow-orange-500/25">
              <Flame className="w-5 h-5 text-white" strokeWidth={2.5} />
            </div>
            <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-emerald-400 border-2 border-white" />
          </div>
          <div className="flex flex-col">
            <span className="font-bold text-base text-slate-800 leading-tight tracking-tight">
              炽竞<span className="text-orange-500 mx-1">·</span>运营中心
            </span>
            <span className="text-[10px] text-slate-400 font-semibold tracking-[0.15em] leading-tight mt-0.5">
              OPERATION PLATFORM
            </span>
          </div>
        </div>
      )}
      {collapsed && (
        <div className="relative">
          <div className="w-10 h-10 rounded-xl bg-linear-to-br from-orange-500 via-amber-500 to-yellow-400 flex items-center justify-center shadow-lg shadow-orange-500/25">
            <Flame className="w-5 h-5 text-white" strokeWidth={2.5} />
          </div>
        </div>
      )}
      {!collapsed && (
        <Button
          type="text"
          icon={<ChevronLeft className="w-4 h-4" />}
          onClick={onToggleCollapse}
          className="text-slate-300 hover:text-slate-500! h-8 w-8 rounded-lg"
        />
      )}
    </div>
  );
};

interface SidebarNavProps {
  menus: NavItem[];
  pathname: string;
  collapsed: boolean;
  expandedMenus: string[];
  onToggleExpand: (name: string) => void;
}

/** 侧栏导航菜单 */
export const SidebarNav = ({
  menus,
  pathname,
  collapsed,
  expandedMenus,
  onToggleExpand,
}: SidebarNavProps) => {
  // 精确匹配或详情子路径（如 /feedback/123）均视为当前菜单高亮
  const isActive = (href: string) => pathname === href || pathname.startsWith(`${href}/`);
  const isParentActive = (children?: NavItem[]) =>
    children?.some((child) => pathname === child.href || pathname.startsWith(`${child.href}/`));

  return (
    <nav className="flex-1 px-4 pb-4 overflow-y-auto sidebar-scroll">
      {menus.map((item, index) => {
        const hasChildren = Boolean(item.children?.length);
        const parentActive = isParentActive(item.children);
        const currentExpanded = expandedMenus.includes(item.name);
        const active = isActive(item.href) || parentActive;
        const showDivider = index === 0;

        if (collapsed) {
          const content = (
            <div
              className={cn(
                "flex items-center justify-center w-full py-3 rounded-xl transition-all duration-200 cursor-pointer relative",
                active
                  ? "bg-linear-to-r from-orange-50 to-amber-50 text-orange-600"
                  : "text-slate-400 hover:text-slate-600 hover:bg-slate-50",
              )}
            >
              {active && (
                <div className="absolute left-0 top-1/2 -translate-y-1/2 w-0.75 h-5 rounded-r-full bg-linear-to-b from-orange-500 to-amber-400" />
              )}
              {item.icon && <IconComponent name={item.icon} className="w-5 h-5 shrink-0" />}
            </div>
          );
          return (
            <Tooltip key={item.href} title={item.name} placement="right">
              {hasChildren ? (
                <div onClick={() => onToggleExpand(item.name)}>{content}</div>
              ) : (
                <Link to={item.href}>{content}</Link>
              )}
            </Tooltip>
          );
        }

        return (
          <div key={item.name} className={cn(index > 0 && "mt-1")}>
            {showDivider && <div className="my-3 mx-1 border-t border-slate-100" />}
            {hasChildren ? (
              <>
                <button
                  type="button"
                  onClick={() => onToggleExpand(item.name)}
                  className={cn(
                    "w-full flex items-center gap-3 px-3.5 py-2.5 rounded-xl transition-all duration-200 relative group",
                    parentActive
                      ? "bg-linear-to-r from-orange-50 to-amber-50 text-orange-700"
                      : "text-slate-500 hover:text-slate-700 hover:bg-slate-50",
                  )}
                >
                  {parentActive && (
                    <div className="absolute left-0 top-1/2 -translate-y-1/2 w-0.75 h-5 rounded-r-full bg-linear-to-b from-orange-500 to-amber-400" />
                  )}
                  {item.icon && <IconComponent name={item.icon} className="w-4.5 h-4.5 shrink-0" />}
                  <span className="flex-1 text-left text-[13.5px] font-medium">{item.name}</span>
                  <ChevronRight
                    className={cn(
                      "w-3.5 h-3.5 transition-transform text-slate-300",
                      currentExpanded && "rotate-90 text-orange-400",
                    )}
                  />
                </button>
                {currentExpanded && (
                  <div className="mt-1 mb-1.5 ml-5 space-y-0.5 pl-3.5 border-l border-slate-100">
                    {item.children?.map((child) => {
                      return (
                        <Link
                          key={child.href}
                          to={child.href}
                          className={cn(
                            "flex items-center gap-2.5 px-3 py-2 rounded-lg transition-all duration-200 text-[13px]",
                            isActive(child.href)
                              ? "text-orange-600 bg-orange-50/60 font-medium"
                              : "text-slate-400 hover:text-slate-600 hover:bg-slate-50",
                          )}
                        >
                          {child.icon && (
                            <IconComponent name={child.icon} className="w-4 h-4 shrink-0" />
                          )}
                          {child.name}
                        </Link>
                      );
                    })}
                  </div>
                )}
              </>
            ) : (
              <Link
                to={item.href}
                className={cn(
                  "flex items-center gap-3 px-3.5 py-2.5 rounded-xl transition-all duration-200 relative group",
                  isActive(item.href)
                    ? "bg-linear-to-r from-orange-50 to-amber-50 text-orange-700"
                    : "text-slate-500 hover:text-slate-700 hover:bg-slate-50",
                )}
              >
                {isActive(item.href) && (
                  <div className="absolute left-0 top-1/2 -translate-y-1/2 w-0.75 h-5 rounded-r-full bg-linear-to-b from-orange-500 to-amber-400" />
                )}
                {item.icon && <IconComponent name={item.icon} className="w-4.5 h-4.5 shrink-0" />}
                <span className="text-[13.5px] font-medium">{item.name}</span>
              </Link>
            )}
          </div>
        );
      })}
    </nav>
  );
};

interface SidebarFooterProps {
  collapsed: boolean;
  onLogout: () => void;
}

/** 侧栏底部退出登录 */
export const SidebarFooter = ({ collapsed, onLogout }: SidebarFooterProps) => {
  const logoutBtn = (
    <button
      type="button"
      onClick={onLogout}
      className={cn(
        "flex items-center rounded-xl text-slate-400 hover:text-red-500 hover:bg-red-50 transition-all duration-200 w-full",
        collapsed ? "justify-center py-3" : "gap-3 px-3.5 py-2.5",
      )}
    >
      <LogOut className={cn("shrink-0", collapsed ? "w-5 h-5" : "w-4.5 h-4.5")} />
      {!collapsed && <span className="text-[13.5px]">退出登录</span>}
    </button>
  );

  return (
    <div className="px-4 pb-4 pt-3 border-t border-slate-100">
      {collapsed ? (
        <Tooltip title="退出登录" placement="right">
          {logoutBtn}
        </Tooltip>
      ) : (
        logoutBtn
      )}
    </div>
  );
};
